import { saveAs } from "file-saver";
import JSZip from "jszip";

export interface DownloadOptions {
  url: string;
  preferredName?: string;
  isPartOfBulk?: boolean;
  maxRetries?: number;
  timeout?: number;
}

export interface DownloadResult {
  success: boolean;
  error?: DownloadError;
  filename?: string;
}

export interface DownloadError {
  type: "NETWORK" | "CORS" | "FILE_SIZE" | "TIMEOUT" | "UNKNOWN";
  message: string;
  url: string;
  retryCount?: number;
}

export interface DownloadProgress {
  id: string;
  status: "pending" | "downloading" | "completed" | "failed";
  progress: number;
  filename?: string;
  error?: DownloadError;
}

// Utility class for download management
export class DownloadManager {
  private static readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  private static readonly MAX_ZIP_SIZE = 200 * 1024 * 1024; // 200MB
  private static readonly DEFAULT_TIMEOUT = 30000; // 30 seconds
  private static readonly DEFAULT_MAX_RETRIES = 3;

  /**
   * Enhanced blob fetching with retry mechanism and timeout
   */
  static async fetchBlobWithRetry(
    url: string,
    maxRetries = this.DEFAULT_MAX_RETRIES,
    timeout = this.DEFAULT_TIMEOUT,
    _onProgress?: (progress: number) => void
  ): Promise<Blob | undefined> {
    const attempts: RequestInit[] = [
      { credentials: "omit", cache: "no-store", redirect: "follow" },
      { credentials: "include", cache: "no-store", redirect: "follow" },
      { mode: "no-cors" as RequestMode, cache: "no-store", redirect: "follow" },
    ];

    for (let retry = 0; retry <= maxRetries; retry++) {
      for (
        let attemptIndex = 0;
        attemptIndex < attempts.length;
        attemptIndex++
      ) {
        const init = attempts[attemptIndex];
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          const response = await fetch(url, {
            ...init,
            referrerPolicy: "no-referrer",
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok && response.type !== "opaque") {
            if (attemptIndex < attempts.length - 1) {
              continue;
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          // Check content length if available (not available in no-cors mode)
          if (response.type !== "opaque") {
            const contentLength = response.headers.get("content-length");
            if (contentLength && parseInt(contentLength) > this.MAX_FILE_SIZE) {
              throw new Error(
                `File size ${parseInt(contentLength)} exceeds ${
                  this.MAX_FILE_SIZE
                } bytes`
              );
            }
          }

          const blob = await response.blob();

          if (blob && blob.size > 0) {
            // Validate file size after download
            if (blob.size > this.MAX_FILE_SIZE) {
              throw new Error(
                `Downloaded file size ${blob.size} exceeds ${this.MAX_FILE_SIZE} bytes`
              );
            }
            return blob;
          }
        } catch (error) {
          if (
            error instanceof Error &&
            error.message.includes("CORS") &&
            attemptIndex < attempts.length - 1
          ) {
            continue;
          }

          // If we've exhausted all attempts for this retry, prepare for next retry
          if (attemptIndex === attempts.length - 1) {
            if (retry < maxRetries) {
              // Exponential backoff before next retry
              await new Promise((resolve) =>
                setTimeout(resolve, Math.pow(2, retry) * 1000)
              );
              break;
            } else {
              throw error; // Final attempt failed, throw the error
            }
          }
        }
      }
    }
    return undefined;
  }

  /**
   * Infers filename from various sources
   */
  static inferFilename(
    url: string,
    preferredName?: string,
    contentTypeExt?: string
  ): string {
    const getExtFromType = (): string => {
      if (!contentTypeExt) return "";
      let ext = contentTypeExt.toLowerCase();
      if (ext === "jpeg") ext = "jpg";
      return ext ? `.${ext}` : "";
    };

    if (preferredName && preferredName.trim().length) {
      const hasExt = /\.[a-zA-Z0-9]{2,5}$/.test(preferredName);
      return hasExt ? preferredName : `${preferredName}${getExtFromType()}`;
    }

    try {
      const u = new URL(url);
      const last = decodeURIComponent(u.pathname.split("/").pop() || "");
      if (last && last.includes(".")) return last;
    } catch (_) {
      // ignore URL parse errors
    }

    return `download${getExtFromType() || ".png"}`;
  }

  static buildForcedDownloadUrl(url: string, filename: string): string {
    try {
      const u = new URL(url);
      const filenameEnc = encodeURIComponent(filename);

      if (
        u.hostname.includes("amazonaws.com") ||
        u.hostname.includes("cloudfront.net") ||
        u.searchParams.has("X-Amz-Signature")
      ) {
        u.searchParams.set(
          "response-content-disposition",
          `attachment; filename="${filenameEnc}"`
        );
        u.searchParams.set("response-content-type", "application/octet-stream");
        return u.toString();
      }

      if (
        u.hostname.includes("storage.googleapis.com") ||
        u.hostname.includes("googleapis.com")
      ) {
        u.searchParams.set(
          "response-content-disposition",
          `attachment; filename=${filenameEnc}`
        );
        u.searchParams.set("response-content-type", "application/octet-stream");
        return u.toString();
      }

      return u.toString();
    } catch {
      return url;
    }
  }

  /**
   * Download single file with enhanced error handling
   */
  static async downloadFile(options: DownloadOptions): Promise<DownloadResult> {
    const {
      url,
      preferredName,
      maxRetries = this.DEFAULT_MAX_RETRIES,
      timeout = this.DEFAULT_TIMEOUT,
    } = options;

    try {
      const blob = await this.fetchBlobWithRetry(url, maxRetries, timeout);

      if (blob) {
        const contentType =
          blob.type && blob.type.includes("/")
            ? blob.type.split("/")[1]
            : undefined;
        const filename = this.inferFilename(url, preferredName, contentType);

        saveAs(blob, filename);

        return {
          success: true,
          filename,
        };
      }

      const guessedFilename = this.inferFilename(url, preferredName);
      const forcedUrl = this.buildForcedDownloadUrl(url, guessedFilename);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = forcedUrl;
      document.body.appendChild(iframe);

      setTimeout(() => {
        if (iframe?.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 5000);

      return {
        success: true,
        filename: guessedFilename,
      };
    } catch (error) {
      // Determine error type based on error message and characteristics
      let errorType: DownloadError["type"] = "UNKNOWN";
      let errorMessage = "Unknown download error";

      if (error instanceof Error) {
        errorMessage = error.message;

        if (error.name === "AbortError") {
          errorType = "TIMEOUT";
        } else if (errorMessage.includes("size")) {
          errorType = "FILE_SIZE";
        } else if (
          errorMessage.includes("CORS") ||
          errorMessage.includes("Access-Control-Allow-Origin") ||
          errorMessage.includes("credentials mode")
        ) {
          errorType = "CORS";
          errorMessage = `CORS error: ${errorMessage}. The server may not allow cross-origin requests with credentials. Try accessing the file directly or contact the administrator.`;
        } else if (
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("HTTP")
        ) {
          errorType = "NETWORK";
        }
      }

      const downloadError: DownloadError = {
        type: errorType,
        message: errorMessage,
        url,
      };

      return {
        success: false,
        error: downloadError,
      };
    }
  }

  /**
   * Process multiple items with concurrency control
   */
  static async processWithConcurrency<T>(
    items: T[],
    processor: (item: T, index: number) => Promise<void>,
    concurrency = 3
  ): Promise<PromiseSettledResult<void>[]> {
    const results: PromiseSettledResult<void>[] = [];

    for (let i = 0; i < items.length; i += concurrency) {
      const batch = items.slice(i, i + concurrency);
      const batchPromises = batch.map((item, batchIndex) =>
        processor(item, i + batchIndex)
      );
      const batchResults = await Promise.allSettled(batchPromises);
      results.push(...batchResults);
    }

    return results;
  }

  /**
   * Create ZIP file from multiple URLs with size validation
   */
  static async createZipFromUrls(
    urls: { url: string; filename: string }[],
    zipFilename: string,
    onProgress?: (current: number, total: number, message: string) => void
  ): Promise<{ success: boolean; blob?: Blob; error?: string }> {
    try {
      const zip = new JSZip();
      let totalSize = 0;
      let processed = 0;

      // Helper function to sanitize filename and remove any path separators
      const sanitizeFilename = (filename: string): string => {
        // Replace path separators (forward slash, backslash) with dash to prevent nested folders
        return filename.replace(/[\\/]/g, "-");
      };

      for (const { url, filename } of urls) {
        onProgress?.(processed, urls.length, `Fetching ${filename}...`);

        const blob = await this.fetchBlobWithRetry(url);
        if (!blob) {
          console.warn(`Failed to fetch ${filename}, skipping...`);
          continue;
        }

        if (totalSize + blob.size > this.MAX_ZIP_SIZE) {
          console.warn(
            `ZIP size limit would be exceeded, stopping at ${processed} files`
          );
          break;
        }

        // Sanitize filename to prevent nested folders in ZIP
        const sanitizedFilename = sanitizeFilename(filename);
        zip.file(sanitizedFilename, blob);
        totalSize += blob.size;
        processed++;
      }

      if (processed === 0) {
        return {
          success: false,
          error: "No files could be added to ZIP",
        };
      }

      onProgress?.(processed, urls.length, "Creating ZIP archive...");

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      return {
        success: true,
        blob: zipBlob,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "ZIP creation failed",
      };
    }
  }
}

export default DownloadManager;
